import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BiX } from "react-icons/bi";
import axios from "axios";
import toast from "react-hot-toast";
import { updateProductType } from "../../redux/thunk";

interface UpdateCategoryModalProps {
  category: {
    id: string;
    name: string;
    image: string;
  };
  onClose: () => void;
  onCategoryUpdated: () => void;
}

export default function UpdateCategoryModal({
  category,
  onClose,
  onCategoryUpdated,
}: UpdateCategoryModalProps) {
  const dispatch = useDispatch<any>();
  const { token } = useSelector((state: any) => state.auth);
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState(category.image);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };


  const GetImageURL = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "image");

    const uploadUrl =
      process.env.REACT_APP_RESTAURANT_UPLOAD_URL ||
      (process.env.REACT_APP_RESTAURANT_URL
        ? `${process.env.REACT_APP_RESTAURANT_URL.replace(/\/v1\/api\/?$/, "").replace(/\/+$/, "")}/upload/file`
        : "https://api.dev.foodbank4u.com/restaurant/upload/file");
    
    try {
      const res = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      return res.data.url;
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast.error(error.response?.data?.message || "Image upload failed");
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageUrl = category.image;

      if (imageFile) {
        console.log("Uploading new image...");
        const uploadedUrl = await GetImageURL(imageFile);
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
          console.log("New image URL:", imageUrl);
        } else {
          // Image upload failed, don't proceed
          toast.error("Image upload failed. Please try again.");
          setLoading(false);
          return;
        }
      }

      console.log("Updating category with image:", imageUrl);
      const response = await dispatch(
        updateProductType({
          id: category.id,
          image: imageUrl,
        })
      ).unwrap();

      toast.success(response?.message || "Category updated successfully");
      onCategoryUpdated();
      onClose();
    } catch (error: any) {
      console.error("Failed to update category:", error);
      toast.error(error.message || "Failed to update category");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Update Category</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <BiX size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category Name
              </label>
              <input
                type="text"
                value={category.name}
                disabled
                className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                Category name cannot be changed.
              </p>
            </div>

            <div>
              <label
                htmlFor="image"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Category Image
              </label>
              
              {previewUrl && (
                <div className="mb-2 h-32 w-full bg-gray-50 rounded-md flex items-center justify-center overflow-hidden border">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="h-full object-contain" 
                  />
                </div>
              )}

              <input
                type="file"
                id="image"
                name="image"
                onChange={handleFileChange}
                accept="image/*"
                className="w-full px-3 py-2 border rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload a new image to update.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
